import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
    Banner, BlockStack, Button, Card, InlineStack, Modal, Select,
    Spinner, Text, TextField,
} from '@shopify/polaris';
import { authenticatedFetch, useAuth } from './AuthComponent';
import { STORE_KEYS, STORE_DISPLAY_NAMES } from './StoreContext';
import { useStore } from './StoreContext';

import { hasRole } from './constants/roles';
/**
 * SkuToolsTab — Settings > SKU Tools
 *
 * Phase 2 (2026-07-23): SKU Notifications only. Swaps section stubbed.
 * Layout mirrors the Order Notifications tab: table of active entries
 * up top, "Add" button, modal for create/edit.
 */
export default function SkuToolsTab() {
    const { user } = useAuth();
    // Follow the store picked in the sidebar (2026-09-08): the card used to
    // start on Soccer Post regardless, so operators searching a Surf SKU
    // with Surf selected up top got "No products found". The card's own
    // dropdown still lets you switch without leaving the page.
    const { activeStore: sidebarStore } = useStore();
    const [activeStore, setActiveStore] = useState(sidebarStore || 'soccerpost');
    useEffect(() => { if (sidebarStore) setActiveStore(sidebarStore); }, [sidebarStore]);
    const [notifications, setNotifications] = useState([]);
    const [allLocs, setAllLocs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState(null);
    const [msgError, setMsgError] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [editing, setEditing] = useState(null); // group_id-or-id being edited

    const flash = (text, err = false) => { setMsg(text); setMsgError(err); };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [r1, r3] = await Promise.all([
                authenticatedFetch(`/api/settings/sku-notifications?store_key=${activeStore}`),
                authenticatedFetch(`/api/locations?store=${activeStore}`),
            ]);
            const j1 = r1.ok ? await r1.json() : { notifications: [] };
            const j3 = r3.ok ? await r3.json() : [];
            setNotifications(j1.notifications || []);
            setAllLocs((j3 || []).filter(l => l.active && l.shopify_location_id !== '0'));
        } finally {
            setLoading(false);
        }
    }, [activeStore]);
    useEffect(() => { load(); }, [load]);

    const handleDelete = async (identOrGroup) => {
        if (!window.confirm('Delete this SKU notification?')) return;
        const r = await authenticatedFetch(
            `/api/settings/sku-notifications/${identOrGroup}`, { method: 'DELETE' });
        if (r.ok) { flash('Deleted.'); load(); }
        else { const e = await r.json().catch(() => ({})); flash(e.detail || 'Delete failed.', true); }
    };

    return (
        <BlockStack gap="400">
            {msg && (
                <Banner tone={msgError ? 'critical' : 'success'} onDismiss={() => setMsg(null)}>{msg}</Banner>
            )}
            <Card>
                <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center">
                        <Text variant="headingMd" as="h3">SKU Notifications</Text>
                        <InlineStack gap="200">
                            <Select
                                label=""
                                labelInline
                                options={STORE_KEYS.map(sk => ({
                                    label: STORE_DISPLAY_NAMES[sk] || sk, value: sk,
                                }))}
                                value={activeStore}
                                onChange={setActiveStore}
                            />
                            <Button variant="primary" onClick={() => { setEditing(null); setShowAdd(true); }}>
                                Add SKU Notification
                            </Button>
                        </InlineStack>
                    </InlineStack>
                    <Text tone="subdued" as="p">
                        A message attached to a SKU that shows on every order containing that SKU
                        — in Order Detail, all portals, and packing slips.
                    </Text>
                    {loading ? <Spinner size="small" /> : (
                        <SkuNotificationTable
                            notifications={notifications}
                            allLocs={allLocs}
                            store={activeStore}
                            onEdit={(gid) => { setEditing(gid); setShowAdd(true); }}
                            onDelete={handleDelete}
                        />
                    )}
                </BlockStack>
            </Card>

            {showAdd && (
                <SkuNotificationModal
                    store={activeStore}
                    existing={editing ? notifications.find(n =>
                        String(n.group_id) === String(editing) ||
                        n.row_ids.map(String).includes(String(editing))) : null}
                    user={user}
                    onClose={() => { setShowAdd(false); setEditing(null); }}
                    onSaved={() => { setShowAdd(false); setEditing(null); load(); flash('Saved.'); }}
                    onError={(e) => flash(e, true)}
                />
            )}

        </BlockStack>
    );
}


function SkuNotificationTable({ notifications, allLocs, store, onEdit, onDelete }) {
    // Load product images for the visible SKUs in one bulk call. Keyed by
    // sorted SKU list so a new/removed notification triggers a re-fetch.
    const [imgs, setImgs] = useState({});
    const skuKey = useMemo(() =>
        [...new Set(notifications.map(n => n.sku))].sort().join(','),
        [notifications]);
    useEffect(() => {
        if (!skuKey) { setImgs({}); return; }
        authenticatedFetch(`/api/sku-images-bulk?skus=${encodeURIComponent(skuKey)}&store=${store}`)
            .then(r => r.ok ? r.json() : { images: {} })
            .then(j => setImgs(j.images || {}))
            .catch(() => setImgs({}));
    }, [skuKey, store]);

    if (!notifications.length) {
        return <Text tone="subdued" as="p">No active SKU notifications.</Text>;
    }
    const nameFor = (sloc) => (allLocs.find(l => l.shopify_location_id === sloc)?.name || sloc);
    const locsLabel = (ids) => {
        if (!ids || ids.length === 0) return 'All locations';
        if (ids.length <= 3) return ids.map(nameFor).join(', ');
        return `${ids.length} locations`;
    };
    return (
        <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%', minWidth: 820 }}>
                <thead>
                    <tr>
                        <th style={{ width: 92 }}></th>
                        <th>SKU</th>
                        <th>Message</th>
                        <th>Locations</th>
                        <th>Club</th>
                        <th>Created</th>
                        <th style={{ width: 160 }}></th>
                    </tr>
                </thead>
                <tbody>
                    {notifications.map(n => {
                        const ident = n.group_id || n.row_ids[0];
                        const img = imgs[n.sku];
                        return (
                            <tr key={ident}>
                                <td>{img
                                    ? <img src={img} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6 }} />
                                    : <div style={{ width: 80, height: 80, background: '#eee', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 32 }}>📦</div>}</td>
                                <td><code>{n.sku}</code></td>
                                <td style={{ maxWidth: 320 }}>{n.message}</td>
                                <td>{locsLabel(n.shopify_location_ids)}</td>
                                <td>{n.club_name || <span className="text-faint">—</span>}</td>
                                <td>{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</td>
                                <td>
                                    <InlineStack gap="100">
                                        <Button size="micro" onClick={() => onEdit(ident)}>Edit</Button>
                                        <Button size="micro" tone="critical" onClick={() => onDelete(ident)}>Delete</Button>
                                    </InlineStack>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}


function SkuNotificationModal({ store, existing, user, onClose, onSaved, onError }) {
    const isEdit = !!existing;
    const [sku, setSku] = useState(existing?.sku || '');
    const [message, setMessage] = useState(existing?.message || '');
    const [scopeMode, setScopeMode] = useState(
        !existing ? 'all' :
        existing.shopify_location_ids?.length === 0 ? 'all' : 'specific'
    );
    const [selectedLocs, setSelectedLocs] = useState(existing?.shopify_location_ids || []);
    const [clubName, setClubName] = useState(existing?.club_name || '');
    const [allLocs, setAllLocs] = useState([]);
    const [availClubs, setAvailClubs] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const isAdmin = hasRole(user, ['admin']);
    const isSiteManager = hasRole(user, ['site_manager']);
    const canBroadcast = isAdmin || isSiteManager;

    // Load locations available to the caller for the active store.
    useEffect(() => {
        (async () => {
            const r = await authenticatedFetch(`/api/locations?store=${store}`);
            if (r.ok) {
                const j = await r.json();
                setAllLocs((j || []).filter(l => l.active && l.shopify_location_id !== '0'));
            }
        })();
    }, [store]);

    // Load clubs when the caller picks a single location (matches Order Notifications UX).
    useEffect(() => {
        if (scopeMode !== 'specific' || selectedLocs.length !== 1) { setAvailClubs([]); return; }
        (async () => {
            const r = await authenticatedFetch(
                `/api/settings/notifications/clubs?store_key=${store}&shopify_location_id=${selectedLocs[0]}`);
            if (r.ok) {
                const j = await r.json();
                setAvailClubs(j.clubs || []);
            }
        })();
    }, [store, scopeMode, selectedLocs]);

    const resolvedScopeWarning = useMemo(() => {
        if (scopeMode === 'all') return `This notification will fire on EVERY order containing this SKU at ALL locations in this store.`;
        if (scopeMode === 'specific' && selectedLocs.length > 1) {
            const names = selectedLocs
                .map(sl => allLocs.find(l => l.shopify_location_id === sl)?.name || sl)
                .join(', ');
            return `This notification will fire on every order at: ${names}.`;
        }
        return null;
    }, [scopeMode, selectedLocs, allLocs]);

    const toggleLoc = (sloc) => {
        setSelectedLocs(cur => cur.includes(sloc) ? cur.filter(x => x !== sloc) : [...cur, sloc]);
    };

    const handleSave = async () => {
        setError(null);
        if (!sku.trim()) return setError('SKU is required.');
        if (!message.trim()) return setError('Message is required.');
        if (scopeMode === 'specific' && !selectedLocs.length) {
            return setError('Pick at least one location, or choose "All locations".');
        }

        const payload = {
            store_key: store,
            sku: sku.trim(),
            message: message.trim(),
            shopify_location_ids: scopeMode === 'all' ? [] : selectedLocs,
            club_name: (selectedLocs.length === 1 && clubName.trim()) ? clubName.trim() : null,
        };

        setSaving(true);
        try {
            let r;
            if (isEdit) {
                // Detect scope-changing edits — the backend PATCH only mutates
                // the message. When locations or club change we delete + create
                // to get the right row set (avoids piling PATCH-scope logic on
                // the API when this UX is rare).
                const origLocs = [...(existing.shopify_location_ids || [])].sort().join(',');
                const newLocs  = [...(scopeMode === 'all' ? [] : selectedLocs)].sort().join(',');
                const origClub = existing.club_name || '';
                const newClub  = payload.club_name || '';
                const scopeChanged = (origLocs !== newLocs) || (origClub !== newClub);

                if (scopeChanged) {
                    const ident = existing.group_id || existing.row_ids[0];
                    const delR = await authenticatedFetch(
                        `/api/settings/sku-notifications/${ident}`, { method: 'DELETE' });
                    if (!delR.ok) {
                        const e = await delR.json().catch(() => ({}));
                        setError(e.detail || 'Delete during scope-change failed.');
                        setSaving(false); return;
                    }
                    r = await authenticatedFetch('/api/settings/sku-notifications', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                    });
                } else {
                    const ident = existing.group_id || existing.row_ids[0];
                    r = await authenticatedFetch(`/api/settings/sku-notifications/${ident}`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ message: payload.message }),
                    });
                }
            } else {
                r = await authenticatedFetch('/api/settings/sku-notifications', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
            }
            if (r.ok) { onSaved(); }
            else {
                const e = await r.json().catch(() => ({}));
                setError(e.detail || 'Save failed.');
            }
        } catch (e) {
            setError(e.message || 'Network error.');
        } finally { setSaving(false); }
    };

    return (
        <Modal
            open
            onClose={onClose}
            title={isEdit ? `Edit SKU Notification` : 'Add SKU Notification'}
            primaryAction={{ content: isEdit ? 'Save' : 'Create', onAction: handleSave, loading: saving }}
            secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
        >
            <Modal.Section>
                <BlockStack gap="300">
                    {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}
                    {isEdit ? (
                        <TextField
                            label="SKU" value={sku} onChange={setSku} disabled
                            autoComplete="off"
                            helpText="SKU can't be changed. Delete and re-add to move to a different SKU."
                        />
                    ) : (
                        <SkuSearchField value={sku} onChange={setSku} store={store} />
                    )}
                    <TextField
                        label="Message"
                        value={message}
                        onChange={setMessage}
                        multiline={2}
                        autoComplete="off"
                        helpText="Shown in red next to the SKU in Order Detail, all portals, and packing slips."
                    />
                    <>
                            <BlockStack gap="100">
                                <Text as="p" variant="bodyMd">Scope</Text>
                                <InlineStack gap="200">
                                    {canBroadcast && (
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <input type="radio" checked={scopeMode === 'all'}
                                                   onChange={() => { setScopeMode('all'); setSelectedLocs([]); setClubName(''); }} />
                                            All locations
                                        </label>
                                    )}
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <input type="radio" checked={scopeMode === 'specific'}
                                               onChange={() => setScopeMode('specific')} />
                                        Specific location(s)
                                    </label>
                                </InlineStack>
                            </BlockStack>

                            {scopeMode === 'specific' && (
                                <BlockStack gap="100">
                                    <Text as="p" variant="bodyMd">Locations</Text>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 4 }}>
                                        {allLocs.map(l => (
                                            <label key={l.shopify_location_id}
                                                   style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <input type="checkbox"
                                                       checked={selectedLocs.includes(l.shopify_location_id)}
                                                       onChange={() => toggleLoc(l.shopify_location_id)} />
                                                {l.name}
                                            </label>
                                        ))}
                                    </div>
                                </BlockStack>
                            )}

                            {scopeMode === 'specific' && selectedLocs.length === 1 && availClubs.length > 0 && (
                                <Select
                                    label="Club (optional — narrows to one club at this location)"
                                    options={[{ label: 'Any club', value: '' },
                                              ...availClubs.map(c => ({ label: c, value: c }))]}
                                    value={clubName}
                                    onChange={setClubName}
                                />
                            )}

                            {resolvedScopeWarning && (
                                <Banner tone="warning">{resolvedScopeWarning}</Banner>
                            )}
                        </>
                </BlockStack>
            </Modal.Section>
        </Modal>
    );
}


/**
 * Type-ahead SKU search. Debounced against /api/products/search-catalog which
 * returns products + variants. Suggestions list "<Product title> — <Variant>
 * — SKU". Click a suggestion to lock the SKU in.
 */
function SkuSearchField({ value, onChange, store, label = 'SKU' }) {
    const [query, setQuery] = useState(value || '');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const debounceRef = useRef(null);

    useEffect(() => { setQuery(value || ''); }, [value]);

    const runSearch = useCallback((q) => {
        if (!q || q.trim().length < 2) { setResults([]); return; }
        setLoading(true);
        // Use the SKU-specific search endpoint: returns individual variants
        // with SKU + product title + variant title + image. (Not the catalog
        // search which dedupes to products and drops variants.)
        authenticatedFetch(`/api/sku-search?q=${encodeURIComponent(q.trim())}&store=${store}`)
            .then(r => r.ok ? r.json() : { results: [] })
            .then(j => {
                setResults((j.results || []).map(r => ({
                    sku:          r.sku,
                    productTitle: r.product_title,
                    variantTitle: r.variant_title,
                    image:        r.image,
                })));
            })
            .catch(() => setResults([]))
            .finally(() => setLoading(false));
    }, [store]);

    const handleType = (v) => {
        setQuery(v);
        onChange(v);  // let the user pick a partial as-is if they want
        setOpen(true);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => runSearch(v), 250);
    };

    return (
        <div style={{ position: 'relative' }}>
            <TextField
                label={label}
                value={query}
                onChange={handleType}
                autoComplete="off"
                onFocus={() => setOpen(true)}
                helpText="Type to search catalog by SKU, barcode, or product title. Pick a suggestion or type an exact SKU."
            />
            {open && query.trim().length >= 2 && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0,
                    // High z-index so the dropdown overlays anything below —
                    // Polaris Modal renders around z-index 520; use 10000 to
                    // stay on top of the Message field and Scope selector.
                    zIndex: 10000, background: '#fff', border: '1px solid #ccc',
                    borderRadius: 4, maxHeight: 320, overflowY: 'auto',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
                }}>
                    {loading && <div style={{ padding: 10 }}>Searching…</div>}
                    {!loading && results.length === 0 && (
                        <div style={{ padding: 10, color: '#666' }}>
                            No products found for “{query.trim()}”. You can still type an exact SKU.
                        </div>
                    )}
                    {!loading && results.map((r, i) => (
                        <div key={r.sku + ':' + i}
                             style={{ padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid #f2f2f2', display: 'flex', gap: 10, alignItems: 'center', minHeight: 52 }}
                             onMouseDown={(e) => {
                                 e.preventDefault();
                                 setQuery(r.sku);
                                 onChange(r.sku);
                                 setOpen(false);
                             }}>
                            {r.image
                                ? <img src={r.image} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                                : <div style={{ width: 40, height: 40, background: '#eee', borderRadius: 4, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 20 }}>📦</div>}
                            <div style={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>
                                <div style={{ fontWeight: 600 }}>
                                    {r.productTitle || <span style={{ color: '#999', fontStyle: 'italic' }}>(no title)</span>}
                                    {r.variantTitle && <span style={{ color: '#666', fontWeight: 400 }}> — {r.variantTitle}</span>}
                                </div>
                                <code style={{ fontSize: '0.85em', color: '#555' }}>{r.sku}</code>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
